import React, { useEffect, useState } from "react";
import { MdDelete, MdInbox } from "react-icons/md";
import {
  addAllocation,
  deleteAllocation,
  getAllocationsByProject,
  getOverrides,
  upsertOverride,
  deleteOverride,
  getUnavailability,
  addUnavailability,
  deleteUnavailability,
} from "../../../../services/capacityApi";
import {
  CapacityOverrideViewModel,
  UnavailabilityType,
  UnavailabilityViewModel,
  UNAVAILABILITY_LABELS,
  WeeklyAllocationViewModel,
} from "../../../../interfaces/Capacity";
import EstimateBridge from "./EstimateBridge";
import IncludeTasksInWeek from "./IncludeTasksInWeek";

interface Option {
  id: number;
  name: string;
}

interface Props {
  employees: Option[];
  projects: Option[];
  onChanged: () => void;
}

const labelCls = "mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300";

const fieldCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const primaryBtnCls =
  "dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50";

const tableWrapCls =
  "overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800";

const thCls =
  "px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300";

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");

const CapacityTeam: React.FC<Props> = ({ employees, projects, onChanged }) => {
  // Alocações
  const [allocProject, setAllocProject] = useState<string>("");
  const [allocEmployee, setAllocEmployee] = useState<string>("");
  const [allocWeek, setAllocWeek] = useState<string>("");
  const [allocHours, setAllocHours] = useState<string>("");
  const [allocations, setAllocations] = useState<WeeklyAllocationViewModel[]>([]);

  // Overrides
  const [overrides, setOverrides] = useState<CapacityOverrideViewModel[]>([]);
  const [ovEmployee, setOvEmployee] = useState<string>("");
  const [ovWeek, setOvWeek] = useState<string>("");
  const [ovHours, setOvHours] = useState<string>("");
  const [ovReason, setOvReason] = useState<string>("");

  // Indisponibilidade
  const [unav, setUnav] = useState<UnavailabilityViewModel[]>([]);
  const [unEmployee, setUnEmployee] = useState<string>("");
  const [unType, setUnType] = useState<number>(UnavailabilityType.Vacation);
  const [unStart, setUnStart] = useState<string>("");
  const [unEnd, setUnEnd] = useState<string>("");
  const [unDesc, setUnDesc] = useState<string>("");

  const reloadOverrides = () => getOverrides(undefined, 12).then(setOverrides);
  const reloadUnav = () => getUnavailability().then(setUnav);
  const reloadAllocations = (projectId: number) =>
    getAllocationsByProject(projectId).then(setAllocations);

  useEffect(() => {
    reloadOverrides();
    reloadUnav();
  }, []);

  useEffect(() => {
    if (allocProject) reloadAllocations(Number(allocProject));
    else setAllocations([]);
  }, [allocProject]);

  const submitAllocation = async () => {
    if (!allocProject || !allocEmployee || !allocWeek || !allocHours) return;
    await addAllocation({
      employeeId: Number(allocEmployee),
      projectId: Number(allocProject),
      weekStart: allocWeek,
      allocatedHours: Number(allocHours),
    });
    setAllocHours("");
    reloadAllocations(Number(allocProject));
    onChanged();
  };

  const submitOverride = async () => {
    if (!ovEmployee || !ovWeek || !ovHours) return;
    await upsertOverride({
      employeeId: Number(ovEmployee),
      weekStart: ovWeek,
      capacityHours: Number(ovHours),
      reason: ovReason || undefined,
    });
    setOvHours("");
    setOvReason("");
    reloadOverrides();
    onChanged();
  };

  const submitUnav = async () => {
    if (!unEmployee || !unStart || !unEnd) return;
    await addUnavailability({
      employeeId: Number(unEmployee),
      type: unType,
      startDate: unStart,
      endDate: unEnd,
      description: unDesc || undefined,
    });
    setUnDesc("");
    reloadUnav();
    onChanged();
  };

  return (
    <div className="flex flex-col gap-8">
      {/* ── Ponte estimativa → alocação ─────────────────────────── */}
      <EstimateBridge projects={projects} onChanged={onChanged} />

      {/* ── Incluir tarefas existentes na semana ─────────────────── */}
      <IncludeTasksInWeek projects={projects} onChanged={onChanged} />

      {/* ── Alocações ───────────────────────────────────────────── */}
      <section>
        <h4 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
          Alocação semanal
        </h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <label htmlFor="alloc-project" className={labelCls}>
              Projeto <span className="text-red-500">*</span>
            </label>
            <select
              id="alloc-project"
              className={fieldCls}
              value={allocProject}
              onChange={(e) => setAllocProject(e.target.value)}
            >
              <option value="">Projeto…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="alloc-employee" className={labelCls}>
              Colaborador <span className="text-red-500">*</span>
            </label>
            <select
              id="alloc-employee"
              className={fieldCls}
              value={allocEmployee}
              onChange={(e) => setAllocEmployee(e.target.value)}
            >
              <option value="">Colaborador…</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="alloc-week" className={labelCls}>
              Semana <span className="text-red-500">*</span>
            </label>
            <input
              id="alloc-week"
              type="date"
              className={fieldCls}
              value={allocWeek}
              onChange={(e) => setAllocWeek(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="alloc-hours" className={labelCls}>
              Horas <span className="text-red-500">*</span>
            </label>
            <input
              id="alloc-hours"
              type="number"
              min={0}
              step={0.5}
              placeholder="Horas"
              className={fieldCls}
              value={allocHours}
              onChange={(e) => setAllocHours(e.target.value)}
            />
          </div>
          <div className="flex items-end sm:col-span-2 lg:col-span-5">
            <button type="button" onClick={submitAllocation} className={primaryBtnCls}>
              Alocar
            </button>
          </div>
        </div>

        {allocProject && (
          <div className={`mt-3 ${tableWrapCls}`}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-navy-900">
                <tr>
                  <th className={thCls}>Colaborador</th>
                  <th className={thCls}>Semana</th>
                  <th className={`${thCls} text-right`}>Horas</th>
                  <th className={`${thCls} w-10`}></th>
                </tr>
              </thead>
              <tbody>
                {allocations.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                  >
                    <td className="px-4 py-3 text-navy-700 dark:text-white">{a.employeeName}</td>
                    <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-300">{fmtDate(a.weekStart)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{a.allocatedHours}h</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        aria-label="Excluir alocação"
                        onClick={async () => {
                          await deleteAllocation(a.id);
                          reloadAllocations(Number(allocProject));
                          onChanged();
                        }}
                        className="text-gray-400 transition-colors hover:text-red-600"
                      >
                        <MdDelete className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
                {allocations.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-700 dark:text-gray-300">
                        <MdInbox className="h-6 w-6 text-gray-400" aria-hidden="true" />
                        Sem alocações para este projeto.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Overrides de capacity ───────────────────────────────── */}
      <section>
        <h4 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
          Ajuste de capacity (override por semana)
        </h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="ov-employee" className={labelCls}>
              Colaborador <span className="text-red-500">*</span>
            </label>
            <select
              id="ov-employee"
              className={fieldCls}
              value={ovEmployee}
              onChange={(e) => setOvEmployee(e.target.value)}
            >
              <option value="">Colaborador…</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ov-week" className={labelCls}>
              Semana <span className="text-red-500">*</span>
            </label>
            <input
              id="ov-week"
              type="date"
              className={fieldCls}
              value={ovWeek}
              onChange={(e) => setOvWeek(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="ov-hours" className={labelCls}>
              Capacity (h) <span className="text-red-500">*</span>
            </label>
            <input
              id="ov-hours"
              type="number"
              min={0}
              step={0.5}
              placeholder="Capacity (h)"
              className={fieldCls}
              value={ovHours}
              onChange={(e) => setOvHours(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="ov-reason" className={labelCls}>
              Motivo
            </label>
            <input
              id="ov-reason"
              placeholder="Motivo (opcional)"
              className={fieldCls}
              value={ovReason}
              onChange={(e) => setOvReason(e.target.value)}
            />
          </div>
          <div className="flex items-end sm:col-span-2 lg:col-span-4">
            <button type="button" onClick={submitOverride} className={primaryBtnCls}>
              Salvar
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {overrides.map((o) => (
            <div
              key={o.id}
              className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-1.5 text-xs dark:bg-navy-900"
            >
              <span className="font-semibold text-navy-700 dark:text-white">{o.employeeName}</span>
              <span className="text-gray-700 dark:text-gray-300">{fmtDate(o.weekStart)} · {o.capacityHours}h</span>
              {o.reason && <span className="text-gray-700 dark:text-gray-300">({o.reason})</span>}
              <button
                type="button"
                aria-label="Excluir override"
                onClick={async () => {
                  await deleteOverride(o.id);
                  reloadOverrides();
                  onChanged();
                }}
                className="text-gray-400 transition-colors hover:text-red-600"
              >
                <MdDelete className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
          {overrides.length === 0 && (
            <span className="text-xs text-gray-700 dark:text-gray-300">Nenhum override nas próximas semanas.</span>
          )}
        </div>
      </section>

      {/* ── Indisponibilidade ───────────────────────────────────── */}
      <section>
        <h4 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
          Indisponibilidade (férias, atestado, eventos)
        </h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label htmlFor="un-employee" className={labelCls}>
              Colaborador <span className="text-red-500">*</span>
            </label>
            <select
              id="un-employee"
              className={fieldCls}
              value={unEmployee}
              onChange={(e) => setUnEmployee(e.target.value)}
            >
              <option value="">Colaborador…</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="un-type" className={labelCls}>
              Tipo
            </label>
            <select
              id="un-type"
              className={fieldCls}
              value={unType}
              onChange={(e) => setUnType(Number(e.target.value))}
            >
              {Object.entries(UNAVAILABILITY_LABELS).map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="un-start" className={labelCls}>
              Início <span className="text-red-500">*</span>
            </label>
            <input
              id="un-start"
              type="date"
              className={fieldCls}
              value={unStart}
              onChange={(e) => setUnStart(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="un-end" className={labelCls}>
              Fim <span className="text-red-500">*</span>
            </label>
            <input
              id="un-end"
              type="date"
              className={fieldCls}
              value={unEnd}
              onChange={(e) => setUnEnd(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="un-desc" className={labelCls}>
              Descrição
            </label>
            <input
              id="un-desc"
              placeholder="Descrição (opcional)"
              className={fieldCls}
              value={unDesc}
              onChange={(e) => setUnDesc(e.target.value)}
            />
          </div>
          <div className="flex items-end sm:col-span-2 lg:col-span-5">
            <button type="button" onClick={submitUnav} className={primaryBtnCls}>
              Registrar
            </button>
          </div>
        </div>

        <div className={`mt-3 ${tableWrapCls}`}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-navy-900">
              <tr>
                <th className={thCls}>Colaborador</th>
                <th className={thCls}>Tipo</th>
                <th className={thCls}>Período</th>
                <th className={thCls}>Descrição</th>
                <th className={`${thCls} w-10`}></th>
              </tr>
            </thead>
            <tbody>
              {unav.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                >
                  <td className="px-4 py-3 text-navy-700 dark:text-white">{u.employeeName}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{UNAVAILABILITY_LABELS[u.type] ?? u.typeName}</td>
                  <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-300">{fmtDate(u.startDate)} – {fmtDate(u.endDate)}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{u.description ?? "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      aria-label="Excluir indisponibilidade"
                      onClick={async () => {
                        await deleteUnavailability(u.id);
                        reloadUnav();
                        onChanged();
                      }}
                      className="text-gray-400 transition-colors hover:text-red-600"
                    >
                      <MdDelete className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
              {unav.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center gap-2 text-gray-700 dark:text-gray-300">
                      <MdInbox className="h-6 w-6 text-gray-400" aria-hidden="true" />
                      Nenhum registro de indisponibilidade.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default CapacityTeam;
